import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { LifeBuoy, Search, Plus, X, ChevronDown, MessageSquare, Lock } from "lucide-react";

const STATUS_STYLES = {
  open: "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20",
  pending: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  resolved: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  closed: "text-[#7d8590] bg-[#1c2128] border-[#30363d]",
};

const EMPTY_TICKET_FORM = { subject: "", body: "", priority: "normal" };

export default function HelpCenter() {
  const [tab, setTab] = useState("articles"); // "articles" | "tickets"
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [openArticle, setOpenArticle] = useState(null);

  const [tickets, setTickets] = useState([]);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [ticketForm, setTicketForm] = useState(EMPTY_TICKET_FORM);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const articlesReqRef = useRef(0);

  const loadArticles = useCallback(async () => {
    // Bumping this invalidates any still-in-flight load, so a slower
    // response to an earlier search can't overwrite newer results.
    const reqId = ++articlesReqRef.current;
    try {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      if (category) params.set("category", category);
      const [aRes, cRes] = await Promise.all([
        api.get(`/help/articles?${params.toString()}`),
        api.get("/help/categories"),
      ]);
      if (reqId !== articlesReqRef.current) return;
      setArticles(aRes.data || []);
      setCategories(cRes.data || []);
    } catch {
      if (reqId === articlesReqRef.current) toast.error("Failed to load help articles");
    }
  }, [query, category]);

  const loadTickets = useCallback(async () => {
    try {
      const { data } = await api.get("/help/tickets");
      setTickets(data || []);
    } catch {
      toast.error("Failed to load tickets");
    }
  }, []);

  // Only reload on tab/category change — the search box itself only
  // searches on blur/Enter (below), not on every keystroke. Using a ref
  // for the latest loadArticles keeps this effect from also re-firing
  // whenever `query` changes `loadArticles`'s identity.
  const loadArticlesRef = useRef(loadArticles);
  useEffect(() => { loadArticlesRef.current = loadArticles; }, [loadArticles]);
  useEffect(() => { if (tab === "articles") loadArticlesRef.current(); }, [tab, category]);
  useEffect(() => { if (tab === "tickets") loadTickets(); }, [tab, loadTickets]);

  const viewArticle = async (article) => {
    try {
      const { data } = await api.get(`/help/articles/${article.article_id}`);
      setOpenArticle(data);
    } catch {
      toast.error("Failed to load article");
    }
  };

  const createTicket = async () => {
    if (!ticketForm.subject.trim() || !ticketForm.body.trim()) return toast.error("Subject and description are required");
    setBusy(true);
    try {
      await api.post("/help/tickets", ticketForm);
      toast.success("Ticket submitted");
      setShowNewTicket(false);
      setTicketForm(EMPTY_TICKET_FORM);
      loadTickets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit ticket");
    } finally {
      setBusy(false);
    }
  };

  const openTicket = async (ticket) => {
    try {
      const { data } = await api.get(`/help/tickets/${ticket.ticket_id}`);
      setTicketDetail(data);
    } catch {
      toast.error("Failed to load ticket");
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/help/tickets/${ticketDetail.ticket_id}/messages`, { body: reply });
      setReply("");
      const { data } = await api.get(`/help/tickets/${ticketDetail.ticket_id}`);
      setTicketDetail(data);
      loadTickets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send reply");
    } finally {
      setBusy(false);
    }
  };

  const closeTicket = async () => {
    try {
      await api.patch(`/help/tickets/${ticketDetail.ticket_id}`, { status: "closed" });
      toast.success("Ticket closed");
      const { data } = await api.get(`/help/tickets/${ticketDetail.ticket_id}`);
      setTicketDetail(data);
      loadTickets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to close ticket");
    }
  };

  if (openArticle) {
    return (
      <DarkPage><div className="p-6 max-w-3xl mx-auto">
        <button onClick={() => setOpenArticle(null)} className="text-xs text-[#7d8590] hover:text-white mb-4 flex items-center gap-1">
          <ChevronDown className="w-3 h-3 rotate-90" /> Back to Help Center
        </button>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#7d8590] mb-2">{openArticle.category}</div>
        <h1 className="text-2xl font-bold text-white mb-4">{openArticle.title}</h1>
        <div className="text-sm text-[#c9d1d9] whitespace-pre-wrap leading-relaxed">{openArticle.body}</div>
      </div></DarkPage>
    );
  }

  if (ticketDetail) {
    const canClose = ticketDetail.status !== "closed";
    return (
      <DarkPage><div className="p-6 max-w-2xl mx-auto">
        <button onClick={() => setTicketDetail(null)} className="text-xs text-[#7d8590] hover:text-white mb-4 flex items-center gap-1">
          <ChevronDown className="w-3 h-3 rotate-90" /> Back to tickets
        </button>
        <div className="flex items-start justify-between mb-4">
          <h1 className="text-xl font-bold text-white">{ticketDetail.subject}</h1>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[ticketDetail.status]}`}>{ticketDetail.status}</span>
            {canClose && (
              <button onClick={closeTicket} className="text-xs px-3 py-1.5 rounded-full border border-[#30363d] text-[#7d8590] hover:text-white">Close</button>
            )}
          </div>
        </div>
        <div className="space-y-3 mb-4">
          {ticketDetail.messages.map(m => (
            <div key={m.message_id} className="bg-[#0d1117] border border-[#21262d] rounded-lg p-4">
              <div className="text-xs text-[#7d8590] mb-1 flex items-center gap-1.5">
                <span className="text-white font-medium">{m.author_name}</span> · {new Date(m.created_at).toLocaleString()}
                {m.is_internal_note && <span className="flex items-center gap-1 text-amber-400"><Lock className="w-3 h-3" /> internal note</span>}
              </div>
              <div className="text-sm text-[#c9d1d9] whitespace-pre-wrap">{m.body}</div>
            </div>
          ))}
        </div>
        {ticketDetail.status !== "closed" && (
          <div className="space-y-2">
            <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} placeholder="Write a reply…"
              className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none" />
            <Button onClick={sendReply} disabled={busy} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
              {busy ? "Sending…" : "Send reply"}
            </Button>
          </div>
        )}
      </div></DarkPage>
    );
  }

  return (
    <DarkPage><div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><LifeBuoy className="w-5 h-5 text-[#7d8590]" /> Help Center</h1>
        <p className="text-sm text-[#7d8590] mt-1">Browse how-to articles, or reach the support team directly.</p>
      </div>

      <div className="flex bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden w-fit mb-6">
        {[["articles", "Articles"], ["tickets", "My tickets"]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm transition-colors ${tab === key ? "bg-[#1c2128] text-white font-medium" : "text-[#7d8590] hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "articles" ? (
        <>
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-[#7d8590] absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={query} onChange={e => setQuery(e.target.value)} onBlur={loadArticles} onKeyDown={e => e.key === "Enter" && loadArticles()}
                placeholder="Search articles…"
                className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md pl-9 pr-3 py-2 text-sm" />
            </div>
            <select value={category} onChange={e => { setCategory(e.target.value); }}
              className="bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
              <option value="">All categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {articles.length === 0 ? (
            <div className="text-center py-16 text-[#7d8590] text-sm">No articles found.</div>
          ) : (
            <div className="space-y-2">
              {articles.map(a => (
                <button key={a.article_id} onClick={() => viewArticle(a)}
                  className="w-full text-left bg-[#0d1117] border border-[#21262d] hover:border-[#00F5D4]/30 rounded-lg p-4 transition-colors">
                  <div className="text-white font-medium">{a.title}</div>
                  <div className="text-xs text-[#7d8590] mt-1">{a.category}</div>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex justify-end mb-4">
            <Button onClick={() => { setTicketForm(EMPTY_TICKET_FORM); setShowNewTicket(true); }} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
              <Plus className="w-4 h-4 mr-1" /> New ticket
            </Button>
          </div>
          {tickets.length === 0 ? (
            <div className="text-center py-16 text-[#7d8590] text-sm">No tickets yet.</div>
          ) : (
            <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
              {tickets.map(t => (
                <button key={t.ticket_id} onClick={() => openTicket(t)}
                  className="w-full text-left flex items-center justify-between px-4 py-3 border-b border-[#21262d] last:border-0 hover:bg-[#161b22] transition-colors">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-3.5 h-3.5 text-[#7d8590]" />
                    <span className="text-sm text-white">{t.subject}</span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[t.status]}`}>{t.status}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {showNewTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New support ticket</h2>
              <button onClick={() => setShowNewTicket(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Subject *</label>
                <input value={ticketForm.subject} onChange={e => setTicketForm(f => ({ ...f, subject: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">What's going on? *</label>
                <textarea value={ticketForm.body} onChange={e => setTicketForm(f => ({ ...f, body: e.target.value }))} rows={4}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Priority</label>
                <select value={ticketForm.priority} onChange={e => setTicketForm(f => ({ ...f, priority: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={createTicket} disabled={busy} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {busy ? "Submitting…" : "Submit ticket"}
              </Button>
              <Button onClick={() => setShowNewTicket(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
